import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Lock, CheckCircle2, Circle, ChevronDown, Award, Download, GraduationCap,
  BookOpen, Loader2, ArrowRight, Sparkles, Star, ExternalLink,
} from "lucide-react";
import { LEVELS, ABOUT, levelItemIds, levelItemCount } from "@/lib/learningContent";
import { GUIDE } from "@/lib/learningGuide";
import * as L from "@/lib/learning";

const T = {
  ink: "#141412", sub: "#6C6C69", mute: "#9B9B97", hair: "#E6E6E4",
  paper: "#F3F2EE", card: "#FFFFFF", green: "#1F8A54", greenBg: "#E7F6EE",
  gold: "#9A7B18", goldBg: "#FBF3DA",
};

const LEVEL_IDS = LEVELS.map((lv) => ({ id: lv.id, ids: levelItemIds(lv), count: levelItemCount(lv) }));

function Ring({ pct, size = 44, stroke = 4, color = T.ink }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flex: "none" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.hair} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100}
        style={{ transition: "stroke-dashoffset .5s ease" }} />
    </svg>
  );
}

function HowTo({ guide }) {
  const [open, setOpen] = useState(false);
  if (!guide) return null;
  return (
    <div style={{ marginBottom: 6 }}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="lc-howto-toggle"
        style={{ display: "inline-flex", alignItems: "center", gap: 7, background: open ? T.ink : T.paper, color: open ? "#fff" : T.ink, border: `1px solid ${open ? T.ink : T.hair}`, borderRadius: 999, padding: "6px 13px", fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "background .15s ease" }}>
        <BookOpen size={13} /> {open ? "Hide how-to" : "How to do this"}
        <ChevronDown size={13} style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform .15s ease" }} />
      </button>
      {open ? (
        <div style={{ marginTop: 10, background: T.paper, border: `1px solid ${T.hair}`, borderRadius: 12, padding: "14px 16px" }}>
          {guide.intro ? <div style={{ fontSize: 12.5, color: T.sub, lineHeight: 1.6, marginBottom: 10 }}>{guide.intro}</div> : null}
          <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none", counterReset: "ht", display: "flex", flexDirection: "column", gap: 9 }}>
            {guide.steps.map((s, i) => (
              <li key={i} style={{ display: "flex", gap: 10, fontSize: 12.5, lineHeight: 1.6, color: T.ink }}>
                <span style={{ flex: "none", width: 20, height: 20, borderRadius: 999, background: T.ink, color: "#fff", fontSize: 11, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>{i + 1}</span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

function CheckRow({ id, label, checked, onToggle, disabled }) {
  return (
    <button type="button" data-testid={`lc-item-${id}`} onClick={() => !disabled && onToggle(id, !checked)}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12, width: "100%", textAlign: "left",
        background: "transparent", border: 0, padding: "9px 0", cursor: disabled ? "default" : "pointer",
      }}>
      <span style={{ marginTop: 1, flex: "none" }}>
        {checked
          ? <CheckCircle2 size={19} style={{ color: T.green }} />
          : <Circle size={19} style={{ color: disabled ? T.hair : T.mute }} />}
      </span>
      <span style={{ fontSize: 13.5, lineHeight: 1.55, color: checked ? T.sub : T.ink, textDecoration: checked ? "none" : "none" }}>
        {label}
      </span>
    </button>
  );
}

function QaRow({ id, q, a, checked, onToggle, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ padding: "4px 0", borderBottom: `1px solid ${T.hair}` }}>
      <CheckRow id={id} label={q} checked={checked} onToggle={onToggle} disabled={disabled} />
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`lc-answer-toggle-${id}`}
        style={{ marginLeft: 31, marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: 0, cursor: "pointer", color: T.sub, fontSize: 12, fontWeight: 700 }}>
        <ChevronDown size={13} style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform .15s ease" }} />
        {open ? "Hide answer" : "Show answer"}
      </button>
      {open ? (
        <div style={{ marginLeft: 31, marginBottom: 10, fontSize: 12.5, lineHeight: 1.6, color: T.ink, background: T.paper, border: `1px solid ${T.hair}`, borderRadius: 10, padding: "10px 12px" }}>
          {a}
        </div>
      ) : null}
    </div>
  );
}

export default function LearningCenter() {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authErr, setAuthErr] = useState(false);
  const [active, setActive] = useState(1);
  const clearingRef = useRef(new Set());
  const completingRef = useRef(false);

  useEffect(() => {
    document.title = "AriaOne · Learning Center";
    L.getProgress()
      .then((p) => { setProgress(p); setLoading(false); })
      .catch((e) => { if (e.code === 401) setAuthErr(true); setLoading(false); });
    L.logEvent("open_learning_center", {}).catch(() => {});
  }, []);

  const checked = useMemo(() => progress?.checked || {}, [progress]);
  const cleared = useMemo(() => new Set(progress?.levels_cleared || []), [progress]);

  const isUnlocked = useCallback((lvId) => lvId === 1 || cleared.has(lvId - 1), [cleared]);
  const levelChecked = useCallback((lvId) => {
    const ent = LEVEL_IDS.find((x) => x.id === lvId);
    return ent.ids.filter((i) => checked[i]).length;
  }, [checked]);
  const levelDone = useCallback((lvId) => {
    const ent = LEVEL_IDS.find((x) => x.id === lvId);
    return ent.count > 0 && ent.ids.every((i) => checked[i]);
  }, [checked]);

  const totalItems = LEVEL_IDS.reduce((a, x) => a + x.count, 0);
  const totalChecked = Object.keys(checked).filter((k) => checked[k]).length;
  const overallPct = totalItems ? Math.round((totalChecked / totalItems) * 100) : 0;
  const courseComplete = !!progress?.course_completed_at;

  // Auto-clear a level the moment its checklist is complete; auto-complete the course.
  useEffect(() => {
    if (!progress) return;
    for (const lv of LEVELS) {
      if (levelDone(lv.id) && !cleared.has(lv.id) && !clearingRef.current.has(lv.id)) {
        clearingRef.current.add(lv.id);
        L.clearLevel(lv.id).then(setProgress).catch(() => {}).finally(() => clearingRef.current.delete(lv.id));
        return;
      }
    }
    if (LEVELS.every((lv) => cleared.has(lv.id)) && !courseComplete && !completingRef.current) {
      completingRef.current = true;
      L.completeCourse().then(setProgress).catch(() => {}).finally(() => { completingRef.current = false; });
    }
  }, [progress, cleared, levelDone, courseComplete]);

  const onToggle = useCallback((id, next) => {
    setProgress((p) => ({ ...p, checked: { ...(p?.checked || {}), [id]: next ? new Date().toISOString() : undefined } }));
    L.toggleItem(id, next).then(setProgress).catch(() => {
      setProgress((p) => ({ ...p, checked: { ...(p?.checked || {}), [id]: next ? undefined : new Date().toISOString() } }));
    });
  }, []);

  const downloadCertificate = () => {
    const name = progress?.learner?.name || "AriaOne User";
    const date = new Date(progress?.course_completed_at || Date.now()).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    L.logEvent("cert_download", {}).catch(() => {});
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>AriaOne Certificate — ${name}</title>
<style>
  @page{size:landscape;margin:0}
  *{margin:0;box-sizing:border-box}
  body{font-family:Georgia,'Times New Roman',serif;background:#F3F2EE;display:flex;align-items:center;justify-content:center;min-height:100vh;padding:28px}
  .cert{width:1000px;max-width:100%;background:#fff;border:1px solid #E6E6E4;box-shadow:0 30px 80px rgba(20,20,18,.14);padding:64px 72px;position:relative}
  .frame{position:absolute;inset:18px;border:2px solid #141412}
  .inner{position:relative;text-align:center}
  .kicker{font-family:ui-sans-serif,-apple-system,Segoe UI,Roboto,sans-serif;letter-spacing:.32em;text-transform:uppercase;font-size:12px;color:#9A7B18;font-weight:700}
  h1{font-size:46px;color:#141412;margin:18px 0 6px;letter-spacing:-.01em}
  .sub{font-family:ui-sans-serif,-apple-system,Segoe UI,Roboto,sans-serif;font-size:14px;color:#6C6C69;margin-bottom:34px}
  .name{font-size:40px;color:#141412;border-bottom:2px solid #141412;display:inline-block;padding:0 28px 10px;margin-bottom:10px}
  .desc{font-family:ui-sans-serif,-apple-system,Segoe UI,Roboto,sans-serif;font-size:14px;line-height:1.7;color:#4A4A46;max-width:620px;margin:22px auto 0}
  .foot{display:flex;justify-content:space-between;align-items:flex-end;margin-top:54px;font-family:ui-sans-serif,-apple-system,Segoe UI,Roboto,sans-serif}
  .foot .lab{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#9B9B97}
  .foot .val{font-size:15px;color:#141412;font-weight:700;border-top:1px solid #141412;padding-top:8px;margin-top:40px;min-width:200px}
  .seal{width:92px;height:92px;border-radius:50%;border:2px solid #9A7B18;color:#9A7B18;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;text-align:center;letter-spacing:.08em;line-height:1.3;margin:0 auto}
  @media print{body{background:#fff;padding:0}.cert{box-shadow:none;border:0}}
</style></head><body onload="setTimeout(function(){window.print()},350)">
  <div class="cert"><div class="frame"></div><div class="inner">
    <div class="kicker">AriaOne Certification</div>
    <h1>Certificate of Completion</h1>
    <div class="sub">This certifies that</div>
    <div class="name">${name}</div>
    <div class="desc">has completed the AriaOne Learning Center — all four levels, Junior through Champion — demonstrating self-attested mastery of the Echo, Lumina and revQ product surface and its support practices.</div>
    <div class="foot">
      <div><div class="lab">Awarded</div><div class="val">${date}</div></div>
      <div class="seal">ARIAONE<br/>CHAMPION</div>
      <div><div class="lab">Program</div><div class="val">AriaOne Learning Center</div></div>
    </div>
  </div></div>
</body></html>`);
    w.document.close();
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.paper, display: "flex", alignItems: "center", justifyContent: "center", color: T.sub, gap: 10 }}>
        <Loader2 className="animate-spin" size={18} /> Loading your Learning Center…
      </div>
    );
  }
  if (authErr) {
    return (
      <div style={{ minHeight: "100vh", background: T.paper, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ maxWidth: 420, textAlign: "center", background: T.card, border: `1px solid ${T.hair}`, borderRadius: 16, padding: "36px 32px" }}>
          <GraduationCap size={32} style={{ color: T.ink }} />
          <div style={{ fontSize: 18, fontWeight: 800, color: T.ink, margin: "14px 0 8px" }}>Please sign in first</div>
          <div style={{ fontSize: 13.5, color: T.sub, lineHeight: 1.6 }}>
            Open the Learning Center from the <b>Learning Center</b> button on your Home screen so your progress is saved to your account.
          </div>
        </div>
      </div>
    );
  }

  const lv = LEVELS.find((x) => x.id === active);
  const activeUnlocked = isUnlocked(active);

  return (
    <div style={{ minHeight: "100vh", background: T.paper, color: T.ink, fontFamily: "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif" }} data-testid="learning-center">
      {/* top bar */}
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(243,242,238,.86)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${T.hair}` }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", padding: "14px 24px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: T.ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
            <GraduationCap size={19} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-.01em" }}>AriaOne Learning Center</div>
            <div style={{ fontSize: 11.5, color: T.sub }}>Self-paced product certification · {progress?.learner?.name}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: T.sub, textTransform: "uppercase", letterSpacing: ".1em" }}>Overall</div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>{overallPct}%</div>
            </div>
            <Ring pct={overallPct} />
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "28px 24px 80px" }}>
        {/* hero / about */}
        <div style={{ background: T.card, border: `1px solid ${T.hair}`, borderRadius: 18, padding: "28px 30px", marginBottom: 22 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11.5, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: T.gold, marginBottom: 12 }}>
            <Sparkles size={14} /> Learn the whole platform
          </div>
          <h1 style={{ fontSize: 30, lineHeight: 1.15, letterSpacing: "-.02em", fontWeight: 800, marginBottom: 14 }}>
            Master AriaOne, one level at a time.
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.7, color: T.sub, maxWidth: 820 }}>{ABOUT.blurb}</p>
          {courseComplete ? (
            <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 14, background: T.goldBg, border: `1px solid #EED9A3`, borderRadius: 14, padding: "14px 18px", flexWrap: "wrap" }} data-testid="lc-course-complete">
              <Award size={26} style={{ color: T.gold, flex: "none" }} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 14.5, fontWeight: 800 }}>All four levels cleared — you're AriaOne Champion certified.</div>
                <div style={{ fontSize: 12.5, color: T.sub }}>Download your certificate to keep or print.</div>
              </div>
              <button onClick={downloadCertificate} data-testid="lc-download-cert"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.ink, color: "#fff", border: 0, borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                <Download size={15} /> Download certificate
              </button>
            </div>
          ) : null}
        </div>

        {/* badges strip */}
        <div style={{ display: "flex", gap: 10, marginBottom: 22, flexWrap: "wrap" }}>
          {LEVELS.map((x) => {
            const done = cleared.has(x.id);
            return (
              <div key={x.id} data-testid={`lc-badge-${x.id}`}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 999, border: `1px solid ${done ? "#CFE7D8" : T.hair}`, background: done ? T.greenBg : T.card, color: done ? T.green : T.mute, fontSize: 12.5, fontWeight: 700 }}>
                {done ? <Star size={14} style={{ fill: T.green, color: T.green }} /> : <Star size={14} />}
                {x.tag}
              </div>
            );
          })}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 22, alignItems: "start" }} className="lc-grid">
          {/* level rail */}
          <div style={{ position: "sticky", top: 86, display: "flex", flexDirection: "column", gap: 10 }} className="lc-rail">
            {LEVELS.map((x) => {
              const unlocked = isUnlocked(x.id);
              const done = cleared.has(x.id);
              const cnt = levelChecked(x.id);
              const ent = LEVEL_IDS.find((e) => e.id === x.id);
              const pct = ent.count ? Math.round((cnt / ent.count) * 100) : 0;
              const isActive = active === x.id;
              return (
                <button key={x.id} data-testid={`lc-level-nav-${x.id}`}
                  onClick={() => { setActive(x.id); L.logEvent("level_open", { level: x.id }).catch(() => {}); }}
                  style={{
                    textAlign: "left", background: isActive ? T.ink : T.card, color: isActive ? "#fff" : T.ink,
                    border: `1px solid ${isActive ? T.ink : T.hair}`, borderRadius: 14, padding: "14px 16px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 12, transition: "background .15s ease",
                  }}>
                  <div style={{ position: "relative", flex: "none" }}>
                    <Ring pct={done ? 100 : pct} size={40} stroke={4} color={done ? T.green : (isActive ? "#fff" : T.ink)} />
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800 }}>
                      {done ? <CheckCircle2 size={16} style={{ color: T.green }} /> : (unlocked ? x.id : <Lock size={13} style={{ color: isActive ? "#fff" : T.mute }} />)}
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em", color: isActive ? "rgba(255,255,255,.65)" : T.mute, fontWeight: 700 }}>Level {x.id} · {x.tag}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.3, marginTop: 2 }}>{x.title}</div>
                    <div style={{ fontSize: 11.5, color: isActive ? "rgba(255,255,255,.7)" : T.sub, marginTop: 3 }}>
                      {done ? "Cleared" : unlocked ? `${cnt}/${ent.count} done` : "Locked"}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* content pane */}
          <div style={{ minWidth: 0 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: ".12em", color: T.gold, fontWeight: 700 }}>Level {lv.id} · {lv.tag}</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.01em", margin: "6px 0 6px" }}>{lv.title}</h2>
              <div style={{ fontSize: 13.5, color: T.sub, lineHeight: 1.6 }}><b style={{ color: T.ink }}>Goal.</b> {lv.goal}</div>
              <div style={{ fontSize: 12.5, color: T.mute, marginTop: 4 }}>For: {lv.target}</div>
              {lv.modules?.length ? (
                <div style={{ marginTop: 14 }} data-testid={`lc-modules-${lv.id}`}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em", color: T.mute, fontWeight: 700, marginBottom: 8 }}>Practise in the app</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {lv.modules.map((m) => (
                      <button key={m.path} data-testid={`lc-open-module-${m.path.replace(/\//g, "")}`}
                        onClick={() => L.openModule(m.path)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 999, padding: "7px 13px", fontSize: 12.5, fontWeight: 700, color: T.ink, cursor: "pointer" }}
                        title={`Open ${m.label} in a new tab`}>
                        {m.label} <ExternalLink size={13} style={{ color: T.sub }} />
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {!activeUnlocked ? (
              <div style={{ background: T.goldBg, border: `1px solid #EED9A3`, borderRadius: 14, padding: "14px 18px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="lc-locked">
                <Lock size={20} style={{ color: T.gold, flex: "none" }} />
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 800 }}>Locked for now — but have a read ahead</div>
                  <div style={{ fontSize: 12.5, color: T.sub }}>Finish Level {lv.id - 1} to start ticking these items off. Everything below is open to read and explore.</div>
                </div>
                <button onClick={() => setActive(lv.id - 1)} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.ink, color: "#fff", border: 0, borderRadius: 10, padding: "9px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", flex: "none" }}>
                  Go to Level {lv.id - 1} <ArrowRight size={15} />
                </button>
              </div>
            ) : null}

                {/* reading */}
                <div style={{ background: T.card, border: `1px solid ${T.hair}`, borderRadius: 16, padding: "22px 24px", marginBottom: 18 }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".1em", color: T.ink, marginBottom: 14 }}>
                    <BookOpen size={15} /> Learn
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {lv.reading.map((r, i) => (
                      <div key={i}>
                        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 5 }}>{r.h}</div>
                        <p style={{ fontSize: 13.5, lineHeight: 1.7, color: T.sub, margin: 0 }}>{r.p}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* checklist groups */}
                {lv.groups.map((g) => {
                  const arr = g.items || g.qa || [];
                  const ids = arr.map((_, i) => `${g.id}-${i}`);
                  const done = ids.filter((i) => checked[i]).length;
                  return (
                    <div key={g.id} data-testid={`lc-group-${g.id}`} style={{ background: T.card, border: `1px solid ${T.hair}`, borderRadius: 16, padding: "18px 24px", marginBottom: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 800, flex: 1 }}>{g.title}</div>
                        <div style={{ fontSize: 11.5, fontWeight: 700, color: done === ids.length ? T.green : T.sub, fontVariantNumeric: "tabular-nums", flex: "none" }}>{done}/{ids.length}</div>
                      </div>
                      <HowTo guide={GUIDE[g.id]} />
                      {g.qa
                        ? g.qa.map((it, i) => (
                          <QaRow key={i} id={`${g.id}-${i}`} q={it.q} a={it.a} checked={!!checked[`${g.id}-${i}`]} onToggle={onToggle} disabled={!activeUnlocked} />
                        ))
                        : g.items.map((it, i) => (
                          <div key={i} style={{ borderBottom: i < g.items.length - 1 ? `1px solid ${T.hair}` : "none" }}>
                            <CheckRow id={`${g.id}-${i}`} label={it} checked={!!checked[`${g.id}-${i}`]} onToggle={onToggle} disabled={!activeUnlocked} />
                          </div>
                        ))}
                    </div>
                  );
                })}

                {/* level status footer */}
                <div style={{ marginTop: 18, background: levelDone(lv.id) ? T.greenBg : T.card, border: `1px solid ${levelDone(lv.id) ? "#CFE7D8" : T.hair}`, borderRadius: 16, padding: "18px 22px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }} data-testid={`lc-level-status-${lv.id}`}>
                  {!activeUnlocked ? (
                    <>
                      <Lock size={22} style={{ color: T.mute, flex: "none" }} />
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontSize: 14, fontWeight: 800 }}>Locked for now</div>
                        <div style={{ fontSize: 12.5, color: T.sub }}>You can read everything here and try the how-tos. Finish Level {lv.id - 1} to start ticking these items off.</div>
                      </div>
                      <button onClick={() => setActive(lv.id - 1)} data-testid={`lc-locked-goto-${lv.id}`} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.ink, color: "#fff", border: 0, borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                        Go to Level {lv.id - 1} <ArrowRight size={15} />
                      </button>
                    </>
                  ) : levelDone(lv.id) ? (
                    <>
                      <CheckCircle2 size={24} style={{ color: T.green, flex: "none" }} />
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 800, color: T.green }}>Level {lv.id} cleared — nicely done.</div>
                        <div style={{ fontSize: 12.5, color: T.sub }}>{lv.id < 4 ? `Level ${lv.id + 1} is now unlocked.` : "You've reached the top — Champion."}</div>
                      </div>
                      {lv.id < 4 ? (
                        <button onClick={() => setActive(lv.id + 1)} data-testid={`lc-next-level-${lv.id}`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.ink, color: "#fff", border: 0, borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                          Start Level {lv.id + 1} <ArrowRight size={15} />
                        </button>
                      ) : (courseComplete ? (
                        <button onClick={downloadCertificate} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: T.ink, color: "#fff", border: 0, borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                          <Download size={15} /> Certificate
                        </button>
                      ) : null)}
                    </>
                  ) : (
                    <>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <div style={{ fontSize: 14, fontWeight: 800 }}>{levelChecked(lv.id)} of {LEVEL_IDS.find((e) => e.id === lv.id).count} items checked</div>
                        <div style={{ fontSize: 12.5, color: T.sub }}>Tick every item to clear Level {lv.id}{lv.id < 4 ? ` and unlock Level ${lv.id + 1}` : ""}.</div>
                      </div>
                      <Ring pct={Math.round((levelChecked(lv.id) / LEVEL_IDS.find((e) => e.id === lv.id).count) * 100)} size={40} />
                    </>
                  )}
                </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 820px){
          .lc-grid{grid-template-columns:1fr !important;}
          .lc-rail{position:static !important;flex-direction:row !important;overflow-x:auto;padding-bottom:4px;}
          .lc-rail > button{min-width:230px;}
        }
      `}</style>
    </div>
  );
}
